import type { TFunction } from 'i18next';
import React, { useState } from 'react';
import { ImageSourcePropType, Platform, StyleSheet, Text, View } from 'react-native';

import {
	VipSubscriptionBenefit1,
	VipSubscriptionBenefit2,
	VipSubscriptionBenefit3,
	VipSubscriptionBenefit4,
	VipSubscriptionBenefit5,
	VipSubscriptionBenefit6,
	VipSubscriptionBenefit7,
	VipSubscriptionBenefit8,
	VipSubscriptionFeatureBenefits,
	VipSubscriptionFeatureCardBack,
	VipSubscriptionFeatureEventPass,
	VipSubscriptionFeatureFrame,
	VipSubscriptionLoyalty,
	VipSubscriptionMonthly,
	VipSubscriptionWeekly,
	VipSubscriptionWelcome,
} from '../../../../assets';
import { Package } from '../../../../store/types';
import GoldGradientText from '../../common/GoldGradientText';
import { GradientStops } from '../../common/GradientPlate';

/**
 * What the VIP Club page's phone layout (ProfileVipScreen, Figma V3 6202:33673) and its desktop
 * layout (ProfileVipDesktop, "VIP / Desktop — VIP Club Subscription · V3" 6280:110717) share: the
 * copy, the artwork, the fills and the gold-text helpers.
 */
/** A perk, benefit or reward. The title is pre-broken where the file wraps it. */
export type Item = [ImageSourcePropType, string[], string, string?];

/**
 * An item's copy as translation keys. A title's line breaks are part of its translation (`\n`),
 * so each language breaks its own lines; `descriptionKey` is null where the design has no third
 * line.
 */
type ItemCopy = [
	ImageSourcePropType,
	titleKey: string,
	titleDefault: string,
	descriptionKey: string | null,
	descriptionDefault: string,
];

type RewardCopy = [...ItemCopy, amount: string];

const toItems = (t: TFunction, copy: ItemCopy[]): Item[] =>
	copy.map(([icon, titleKey, titleDefault, descriptionKey, descriptionDefault]) => [
		icon,
		t(titleKey, { defaultValue: titleDefault }).split('\n'),
		descriptionKey ? t(descriptionKey, { defaultValue: descriptionDefault }) : '',
	]);

export const FONT = 'Sofia Sans';
// The file sets the titles and card copy in Sofia Sans Condensed, which every platform bundles
// (platformAssets/runtime/fonts.web.js, res/font, Info.plist).
export const FONT_CONDENSED = 'Sofia Sans Condensed';

const PRIMARY_FEATURES: ItemCopy[] = [
	[
		VipSubscriptionFeatureEventPass,
		'T_SITE_VIP_PERK_EVENT_PASS',
		'Free premium\nevent pass',
		null,
		'',
	],
	[VipSubscriptionFeatureFrame, 'T_SITE_VIP_PERK_FRAME', 'Exclusive\nVIP frame', null, ''],
	[VipSubscriptionFeatureCardBack, 'T_SITE_VIP_PERK_CARD_BACK', 'Exclusive\ncard back', null, ''],
	[VipSubscriptionFeatureBenefits, 'T_SITE_VIP_PERK_ALL_BENEFITS', 'All VIP\nbenefits', null, ''],
];

// Figma V3 "Benefit Grid" (6202:33723).
const BENEFITS: ItemCopy[] = [
	[
		VipSubscriptionBenefit1,
		'T_SITE_VIP_BENEFIT_VIP_ROOM',
		'Access to the\nVIP room',
		'T_SITE_VIP_BENEFIT_VIP_ROOM_DESC',
		'Create tables & choose players',
	],
	[
		VipSubscriptionBenefit2,
		'T_SITE_VIP_BENEFIT_DAILY_BONUS',
		'TWICE daily bonus',
		'T_SITE_VIP_BENEFIT_DAILY_BONUS_DESC',
		'More rewards every day',
	],
	[
		VipSubscriptionBenefit3,
		'T_SITE_VIP_BENEFIT_PHRASES',
		'More and cool phrases',
		'T_SITE_VIP_BENEFIT_PHRASES_DESC',
		'Stand out in the chat',
	],
	[
		VipSubscriptionBenefit4,
		'T_SITE_VIP_BENEFIT_LEVEL_UP',
		'Level up TWICE faster',
		'T_SITE_VIP_BENEFIT_LEVEL_UP_DESC',
		'Progress faster and unlock more',
	],
	[
		VipSubscriptionBenefit5,
		'T_SITE_VIP_BENEFIT_CARD_POWER',
		'You can see the power\nof cards',
		'T_SITE_VIP_BENEFIT_CARD_POWER_DESC',
		'Know the strength in your hand',
	],
	[
		VipSubscriptionBenefit6,
		'T_SITE_VIP_BENEFIT_NAME_CHANGES',
		'No restrictions on name\nchanges',
		'T_SITE_VIP_BENEFIT_NAME_CHANGES_DESC',
		'Change your name anytime',
	],
	[
		VipSubscriptionBenefit7,
		'T_SITE_VIP_BENEFIT_AVATAR_CHANGES',
		'No restrictions on\navatar changes',
		'T_SITE_VIP_BENEFIT_AVATAR_CHANGES_DESC',
		'Change your avatar as often as you want',
	],
	[
		VipSubscriptionBenefit8,
		'T_SITE_VIP_BENEFIT_MORE',
		'And more...',
		'T_SITE_VIP_BENEFIT_MORE_DESC',
		'Future VIP content and surprises!',
	],
];

const REWARDS: RewardCopy[] = [
	[
		VipSubscriptionWelcome,
		'T_SITE_VIP_REWARD_WELCOME',
		'WELCOME\nREWARD',
		'T_SITE_VIP_REWARD_WELCOME_DESC',
		'Exclusive VIP frame + card back',
		'50,000',
	],
	[
		VipSubscriptionWeekly,
		'T_SITE_VIP_REWARD_WEEKLY',
		'WEEKLY\nREWARDS',
		'T_SITE_VIP_REWARD_WEEKLY_DESC',
		'Claim every week',
		'25,000',
	],
	[
		VipSubscriptionMonthly,
		'T_SITE_VIP_REWARD_MONTHLY',
		'MONTHLY\nREWARDS',
		'T_SITE_VIP_REWARD_MONTHLY_DESC',
		'1 Premium Event Pass every renewal',
		'100,000',
	],
	[
		VipSubscriptionLoyalty,
		'T_SITE_VIP_REWARD_LOYALTY',
		'LOYALTY\nREWARDS',
		'T_SITE_VIP_REWARD_LOYALTY_DESC',
		'The longer you stay, the bigger the rewards',
		'250,000',
	],
];

/** Built during render with the screen's `t`, so a language switch re-renders the copy. */
export const benefits = (t: TFunction) => toItems(t, BENEFITS);
export const primaryFeatures = (t: TFunction) => toItems(t, PRIMARY_FEATURES);
export const rewards = (t: TFunction): Item[] =>
	REWARDS.map(([icon, titleKey, titleDefault, descriptionKey, descriptionDefault, amount]) => [
		icon,
		t(titleKey, { defaultValue: titleDefault }).split('\n'),
		descriptionKey ? t(descriptionKey, { defaultValue: descriptionDefault }) : '',
		amount,
	]);

// Figma "Price Panel" (2583:1126) "Base": #330B05 to #0F0402 top to bottom inside a #FFF908 rim.
export const PRICE_PANEL_FILL: GradientStops = [
	['0', '#330B05'],
	['1', '#0F0402'],
];
// "Subscribe Button" (2583:1132): #66D93D through #248F1C at 55% to #0D540F.
export const SUBSCRIBE_FILL: GradientStops = [
	['0', '#66D93D'],
	['0.55', '#248F1C'],
	['1', '#0D540F'],
];
// "Reward Card" (2583:1253): rgb(87,20,5) until 43%, fading to rgb(31,5,3).
export const REWARD_CARD_FILL: GradientStops = [
	['0.4312', '#571405'],
	['1', '#1F0503'],
];
export const REWARD_CARD_BORDER = 'rgba(255,140,31,0.35)';
/**
 * The warm halo the file puts behind the benefit and reward icons (the perk strip's have none). It has to follow the
 * artwork's shape: a CSS drop-shadow on web and Android, the layer shadow on iOS (which traces an
 * image's alpha) - never the web box-shadow RN's shadow props turn into.
 */
export const iconGlow = (radius: number) =>
	Platform.select<object>({
		ios: {
			shadowColor: '#FF8A00',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.75,
			shadowRadius: radius,
		},
		default: { filter: `drop-shadow(0px 0px ${radius}px rgba(255,138,0,0.75))` },
	});

// "Membership extras" plate, sampled from the frame: #9A0501 under the rim fading to #260802 at
// the bottom, inside a #FFF908 rim.
export const PERK_STRIP_FILL: GradientStops = [
	['0', '#9A0501'],
	['1', '#260802'],
];
// Its red inner glow, sampled on all four sides. Figma adds the red, so it lifts the bright top
// and the dark bottom by the same amount; an overlay needs more opacity at the top and less at the
// bottom for that. `GLOW` is the falloff in from the rim (`[distance, opacity]` at design size,
// scaled so the top edge reaches full strength) and `FADE` the top-to-bottom strength.
export const PERK_STRIP_GLOW: Array<[number, number]> = [
	[0, 1],
	[0.5, 0.92],
	[1.5, 0.67],
	[2.5, 0.48],
	[3.5, 0.33],
	[4.5, 0.21],
	[5.5, 0.13],
	[6.5, 0.06],
	[7.5, 0.02],
	[8.5, 0],
];
export const PERK_STRIP_GLOW_FADE: Array<[string, number]> = [
	['0', 1],
	['0.25', 0.78],
	['0.5', 0.63],
	['0.75', 0.53],
	['1', 0.465],
];
// The rim runs pale at the top to full yellow at the bottom.
export const PERK_STRIP_RIM: GradientStops = [
	['0', '#FFFF9D'],
	['0.5', '#FFFC54'],
	['1', '#FFF908'],
];

export type GoldLineStyle = {
	fontSize: number;
	fontFamily?: string;
	fontWeight?: '600' | '900';
	letterSpacing?: number;
	align?: 'left' | 'center';
	shadow?: boolean;
};

/**
 * A line of the gold gradient text, narrowed to fit `maxWidth`. GoldGradientText is SVG and does
 * not size itself, so the line is measured as real text, drawn at that width and squeezed
 * horizontally if it runs over. (SVG `textLength` would do it on web, but react-native-svg
 * ignores it on iOS.)
 */
export const FittedGoldLine = ({
	text,
	maxWidth,
	height,
	fontSize,
	fontFamily = FONT_CONDENSED,
	fontWeight = '900',
	letterSpacing = 0,
	align = 'left',
	shadow = false,
	gradientSpan,
}: GoldLineStyle & {
	text: string;
	maxWidth: number;
	height: number;
	gradientSpan?: { top: number; height: number };
}) => {
	const [natural, setNatural] = useState(0);
	const width = Math.max(natural, 1);
	const squeeze = natural > maxWidth ? maxWidth / natural : 1;
	// Scaling happens about the centre; shift so the line starts at the left, or sits centred.
	const shiftX = align === 'center' ? (maxWidth - width) / 2 : -(width - width * squeeze) / 2;
	return (
		<View style={{ width: maxWidth, height }}>
			<View pointerEvents='none' style={fittedLineStyles.measureBox}>
				<Text
					numberOfLines={1}
					onLayout={event => setNatural(Math.ceil(event.nativeEvent.layout.width))}
					style={[fittedLineStyles.measure, { fontFamily, fontSize, fontWeight, letterSpacing }]}
				>
					{text}
				</Text>
			</View>
			{natural > 0 ? (
				<View style={{ width, height, transform: [{ translateX: shiftX }, { scaleX: squeeze }] }}>
					<GoldGradientText
						text={text}
						width={width}
						height={height}
						fontSize={fontSize}
						fontFamily={fontFamily}
						fontWeight={fontWeight}
						letterSpacing={letterSpacing}
						align='left'
						shadow={shadow}
						gradientSpan={gradientSpan}
					/>
				</View>
			) : null}
		</View>
	);
};

/**
 * A wrapped gold title: the lines stack at `lineHeight` and share one gradient, so the first is
 * pale gold and the last orange, as in the file ("Access to the" / "VIP room").
 */
export const GoldLines = ({
	lines,
	width,
	lineHeight,
	...style
}: GoldLineStyle & { lines: string[]; width: number; lineHeight: number }) => (
	<View>
		{lines.map((line, index) => (
			<FittedGoldLine
				key={line}
				text={line}
				maxWidth={width}
				height={lineHeight}
				gradientSpan={{ top: index * lineHeight, height: lines.length * lineHeight }}
				{...style}
			/>
		))}
	</View>
);

const fittedLineStyles = StyleSheet.create({
	// Laid out to be measured only: never seen, never in the way, and wide enough not to wrap.
	measureBox: { position: 'absolute', left: 0, top: 0, width: 1000, opacity: 0 },
	measure: { alignSelf: 'flex-start' },
});

export const formatPrice = (pkg?: Package) => {
	if (!pkg) return '€4.99';
	const price = Number(pkg.price || 0).toFixed(2);
	const currencyValue = Array.isArray(pkg.currency) ? pkg.currency[0] : pkg.currency;
	const currency = currencyValue?.symbol || currencyValue?.name || currencyValue;
	return currency === 'EUR' || currency === '€' || !currency ? `€${price}` : `${price} ${currency}`;
};
